import { DispatcherEvent } from '@carvana/merch-native';
import { useCallback, useContext } from 'react';
import {
  CTA_GUARD_ACTION,
  CtaGuard,
  CtaGuardOutcome,
  CtaSource
} from '~/constants/common/ctaGuard';
import CtaType from '~/constants/ctaType';
import { useIsNative } from '~/context/common/DeviceContext';
import ModalContext from '~/context/vdp/ModalContext';
import useCtaButtonState from '~/hooks/vdp/selectors/useCtaButtonState';
import { useEventPayload } from '~/hooks/vdp/selectors/useVehicleDetailsForNative';
import { logInfo } from '~/utilities/loggers';
import sendPurchaseMessageToNative from './sendPurchaseMessageToNative';
import useGetStarted from './useGetStarted';
import usePreOrder from './usePreOrder';

const dummyFunction = () => {};

function useClickHandler(): () => void {
  const isNative = useIsNative();
  const { setLockTimerModalOpen } = useContext(ModalContext);
  const { stockNumber, type } = useCtaButtonState();
  const eventPayload = useEventPayload();

  const getStarted = useGetStarted(stockNumber);
  const preOrder = usePreOrder();

  const continuePurchase = useCallback(() => {
    if (isNative) {
      sendPurchaseMessageToNative(
        DispatcherEvent.ContinuePurchase,
        eventPayload
      );
    } else {
      window.location.href = `/purchase/dashboard?stock-number=${stockNumber}`;
    }
  }, [eventPayload, isNative, stockNumber]);

  const notifyMe = useCallback(() => {
    setLockTimerModalOpen(true);
  }, [setLockTimerModalOpen]);

  switch (type) {
    case CtaType.ContinuePurchase:
      return continuePurchase;
    case CtaType.Default:
      return dummyFunction;
    case CtaType.GetStarted:
      return getStarted;
    case CtaType.Loader:
      return dummyFunction;
    case CtaType.NotifyMe:
      return notifyMe;
    case CtaType.Reservable:
      return preOrder;
    default:
      return dummyFunction;
  }
}

function useButtonHandler(onClick: () => void = dummyFunction) {
  const { isDisabled, type } = useCtaButtonState();
  const handleClick = useClickHandler();

  return useCallback(() => {
    // isNoop drives telemetry only. Gating the click on it too would stop an enabled type with no
    // dispatch of its own (Reservation) from running onClick, diverging from usePrimaryButtonHandler.
    const isNoop = isDisabled || handleClick === dummyFunction;

    logInfo(CTA_GUARD_ACTION, {
      ctaSource: CtaSource.PriceDetails,
      ctaType: type,
      guard: CtaGuard.Dispatch,
      outcome: isNoop ? CtaGuardOutcome.Noop : CtaGuardOutcome.Passed
    });

    if (isDisabled) return;

    onClick();
    handleClick();
  }, [handleClick, isDisabled, onClick, type]);
}

export default useButtonHandler;
